import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Fund, STAGE_COLORS } from '../../types/fundraise';
import { FiTrash2 } from 'react-icons/fi';
import { formatAmount } from '../../utils/helpers';

// Type-safe icon wrapper
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface FundCardProps {
  fund: Fund;
  onDelete?: (fund: Fund) => void;
  onClick?: (fund: Fund) => void;
}

const getAmountRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '';
  if (min === null) return formatAmount(max);
  if (max === null) return formatAmount(min);
  return `${formatAmount(min)} - ${formatAmount(max)}`;
};

const getLogoInitials = (name: string, initials?: string | null): string => {
  if (initials) return initials.toUpperCase().slice(0, 2);
  const trimmedName = name.trim();
  if (!trimmedName) return '??';
  const words = trimmedName.split(/\s+/);
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
};

const getLogoColor = (color: string): string => {
  return STAGE_COLORS[color] || '#8b5cf6';
};

const FundCard: React.FC<FundCardProps> = ({ fund, onDelete, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: fund.id,
    data: {
      type: 'fund',
      fund,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(fund);
    }
  };

  // dnd-kit's PointerSensor in KanbanBoard uses activationConstraint distance=8,
  // so a non-drag pointerdown/up sequence (a real click) is not consumed by the
  // drag listeners and onClick still fires here.
  const handleCardClick = () => {
    if (onClick) {
      onClick(fund);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`su-fund-card ${isDragging ? 'dragging' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={onClick ? handleCardClick : undefined}
      {...listeners}
      {...attributes}
    >
      <div
        className="su-fund-card-logo"
        style={{ backgroundColor: getLogoColor(fund.logo_color) }}
      >
        {getLogoInitials(fund.name, fund.logo_initials)}
      </div>
      <div className="su-fund-card-content">
        <div className="su-fund-card-name" title={fund.name}>
          {fund.name}
        </div>
        {(fund.min_amount !== null || fund.max_amount !== null) && (
          <div className="su-fund-card-amount">
            {getAmountRange(fund.min_amount, fund.max_amount)}
          </div>
        )}
      </div>
      {onDelete && (
        <button
          type="button"
          className="su-fund-card-delete"
          onClick={handleDelete}
          title="Delete fund"
          aria-label="Delete fund"
        >
          <TrashIcon width={14} height={14} />
        </button>
      )}
    </div>
  );
};

export default FundCard;
